import * as React from "react";
import { Button } from "~/components/Button";
import { useConfirm } from "~/components/Dialogs";
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByNumber, sortByString } from "~/lib/sort";
import { ILine } from "../models/Line";
import { updateLineStatus, fetchLine } from "../api";

interface ILinesTableProps {
  records: ILine[];
  isPending: boolean;
  agencyId: number;
  onEdit?: (line: ILine) => void;
  onDelete?: (line: ILine) => void;
  onUpdate?: () => void;
}

function normalizeLine(line: any): ILine {
  return {
    ...line,
    id: line.id,
    domainId: line.domainId,
  };
}

export const LinesTable: React.FC<ILinesTableProps> = ({
  records,
  isPending,
  agencyId,
  onEdit,
  onDelete,
  onUpdate,
}) => {
  const confirm = useConfirm();

  const filteredRecords = records.map(normalizeLine);

  const isDeleted = (s?: string) => (s ?? "").toLowerCase() === "deleted";

  return (
    <TableSort<ILine>
      records={filteredRecords}
      header
      columns={[
        { field: "id", header: "ID", sort: sortByNumber, small: true },
        { field: "domain", header: "Ad System Domain", sort: sortByString },
        {
          field: "resellerDomain",
          header: "Seller Domain",
          sort: sortByString,
          cell: (r) => r.resellerDomain ?? "—",
        },
        {
          field: "externalSellerId",
          header: "Seller ID",
          sort: sortByString,
          cell: (r) => r.externalSellerId || "—"
        },
        { field: "sellerType", header: "Type", sort: sortByString },
        { field: "scope", header: "Scope", sort: sortByString },
        { field: "status", header: "Status", sort: sortByString },
        {
          cell: (rawLine) => {
            const line = normalizeLine(rawLine);
            return (
              <TableRowActions>
                {line.status !== "manual" && !isDeleted(line.status) && (
                  <Button
                    small
                    variant="menu"
                    intent={Intent.WARNING}
                    onClick={async () => {
                      try {
                        await updateLineStatus({ agencyId, id: line.id }, "manual");
                        if (onUpdate) onUpdate();
                      } catch (e) {
                        console.error("Failed to update status:", e);
                      }
                    }}
                  >
                    Make Manual
                  </Button>
                )}
                <Button
                  small
                  variant="menu"
                  onClick={async () => {
                    const fullLine = await fetchLine(agencyId, line.id);
                    onEdit?.(fullLine);
                  }}
                  leftIcon={<i className="fa fa-edit" />}
                >
                  Edit
                </Button>
                {!isDeleted(line.status) && onDelete && (
                  <Button
                    small
                    variant="menu"
                    intent={Intent.DANGER}
                    onClick={() =>
                      confirm("Delete this line?", {
                        onOk: () => onDelete({ ...line, agencyId }),
                        icon: <LucideIcon icon={Trash2} size={20} />,
                        intent: Intent.DANGER,
                      })
                    }
                    leftIcon={<i className="fa fa-trash" />}
                  >
                    Delete
                  </Button>
                )}
              </TableRowActions>
            );
          },
          small: true,
        },
      ]}
      placeholder={isPending && <Progress />}
    />
  );
};
